import React from "react";

import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import { PaymentMethod } from "constants/payments";

import { getPaymentMethodsByBuyerId } from "selectors";

import { Chip, ChipBag } from "./BaseChip";

// Payment methods are capped at a handful per buyer, so the bag shouldn't
// stretch to fill the row or reserve a full row's height like other ChipBags.
const CompactChipBag = styled(ChipBag)`
  flex-grow: 0;
  min-height: 0;
  width: fit-content;
`;

// For dense list rows (eg AccountingDayRouting) - no border/margin of its own,
// and a font-size matching the row's other caption-sized text.
const SmallChipBag = styled(CompactChipBag)`
  border: none;
  margin: 0;
  padding: 0;
  font-size: ${({ theme }) => theme.fontSizes.alpha}px;
`;

interface PaymentMethodStyle {
  backgroundColor: string;
  color?: string;
}

const PaymentMethodStyleMap: Record<PaymentMethod, PaymentMethodStyle> = {
  [PaymentMethod.ACCOUNT]: { backgroundColor: "warning", color: "white" },
  [PaymentMethod.CASH]: { backgroundColor: "lightGreen" },
  [PaymentMethod.EFT]: { backgroundColor: "primaryHighlight", color: "white" },
  [PaymentMethod.CARD]: { backgroundColor: "success", color: "white" },
  [PaymentMethod.CHEQUE]: { backgroundColor: "lavender", color: "white" },
};

const DEFAULT_PAYMENT_METHOD_STYLE: PaymentMethodStyle = {
  backgroundColor: "gray95",
};

export const PaymentMethodChips = ({
  buyerId,
  small = false,
}: {
  buyerId: string;
  small?: boolean;
}) => {
  const paymentMethods = useSelector(getPaymentMethodsByBuyerId(buyerId));

  if (!paymentMethods.length) {
    return null;
  }

  const Bag = small ? SmallChipBag : CompactChipBag;

  return (
    <Bag>
      {paymentMethods.map(method => {
        const { backgroundColor, color } =
          PaymentMethodStyleMap[method] ?? DEFAULT_PAYMENT_METHOD_STYLE;
        return (
          <Chip key={method} backgroundColor={backgroundColor} color={color}>
            {method}
          </Chip>
        );
      })}
    </Bag>
  );
};
